import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";

type FAQItem = {
  id: string;
  slug: string;
  title: string;
  snippet: string;
  tags: string[] | null;
  created_at: string;
};

export default function FAQList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const tagParam = searchParams.get("tag") ?? "";

  const [q, setQ] = useState(qParam);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<FAQItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const activeTag = tagParam;

  const load = async () => {
    setLoading(true);
    setError(null);

    const { data, error } = await supabase.rpc("faq_search", {
      p_q: qParam, // Suche serverseitig (Titel/Body/Tags)
      p_limit: 100,
      p_offset: 0,
    });

    if (error) {
      console.error("faq_search failed:", error.message);
      setError("Konnte FAQ nicht laden.");
      setItems([]);
      setLoading(false);
      return;
    }

    setItems((data ?? []) as FAQItem[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam, tagParam]);

  // Tag-Cloud aus Ergebnissen
  const allTags = useMemo(() => {
    const s = new Set<string>();
    items.forEach((i) => (i.tags ?? []).forEach((t) => t && s.add(t)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [items]);

  // Optional: clientseitig zusätzlich nach Tag filtern
  const filtered = useMemo(() => {
    if (!activeTag) return items;
    return items.filter((i) => (i.tags ?? []).includes(activeTag));
  }, [items, activeTag]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (q.trim()) next.set("q", q.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("tag");
    setSearchParams(next, { replace: true });
    setQ("");
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-20 px-4 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">❓ FAQ & Hilfe</h1>
          <p className="text-sm text-slate-400 mt-1">
            Finde Antworten, Tipps & kurze Tutorials.
          </p>
        </div>

        {/* Suche */}
        <form onSubmit={onSearch} className="flex gap-2 mb-4">
          <input
            className="flex-1 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder:text-slate-600"
            placeholder="Suchen (Titel, Inhalt oder Tags)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600"
          >
            Suchen
          </button>
          {(qParam || activeTag) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 rounded-lg border border-[#2a3347] text-slate-300 bg-[#1e2639] hover:bg-[#2a3347]"
              title="Filter zurücksetzen"
            >
              Zurücksetzen
            </button>
          )}
        </form>

        {/* Tag-Filter */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {allTags.map((t) => {
              const isActive = activeTag === t;
              return (
                <button
                  key={t}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (isActive) next.delete("tag");
                    else next.set("tag", t);
                    setSearchParams(next, { replace: true });
                  }}
                  className={`px-2 py-1 rounded-full text-sm border ${
                    isActive
                      ? "bg-sky-500 text-white border-sky-500"
                      : "bg-[#1e2639] text-slate-300 border-[#2a3347] hover:bg-[#2a3347]"
                  }`}
                >
                  #{t}
                </button>
              );
            })}
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <p className="text-slate-400">⏳ Lade Einträge…</p>
        ) : error ? (
          <p className="text-red-400">{error}</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-400">Keine Einträge gefunden.</p>
        ) : (
          <ul className="space-y-3">
            {filtered.map((i) => (
              <li key={i.id} className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:shadow-sm transition">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold">
                    <Link to={`/faq/${i.slug}`} className="text-sky-400 hover:underline">
                      {i.title}
                    </Link>
                  </h2>
                  <span className="text-xs text-slate-500">
                    {new Date(i.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="prose prose-sm prose-invert mt-1 max-w-none text-slate-400">
                  <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                    {i.snippet}
                  </ReactMarkdown>
                </div>

                {(i.tags ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(i.tags ?? []).map((t) => (
                      <span
                        key={t}
                        className="px-2 py-0.5 rounded-full text-xs border border-[#2a3347] bg-[#1e2639] text-slate-400"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-2">
                  <Link to={`/faq/${i.slug}`} className="text-sky-400 hover:underline text-sm">
                    Weiterlesen →
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
